/** @jsx jsx */
import { useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import { Box, Flex, jsx, Text } from 'theme-ui'
import CheckoutButton from '~/components/Checkout/Button'
import GiftMessage from '~/components/Cart/GiftMessage'
import { useShippingMeter } from '~/hooks/components/use-shipping-meter'
import { toUsdCurrency } from '~/utils/formatters'

const CartFooter = ({
  displayItemTotal,
  displayPromotions,
  displayShipTotal,
  displayStoreCreditRemainingAfterCapture,
  displayTaxTotal,
  displayTotal,
  displayTotalApplicableStoreCredit,
  giftNote,
  isBulk,
  isGiftCardsOnly,
  isGiftMessageOverlay,
  itemTotal,
  orderTotalAfterStoreCredit,
  shipTotal,
  totalApplicableStoreCredit,
}) => {
  const { eligibleForFreeShipping } = useShippingMeter()
  const translate = useTranslate()
  const isCreditApplied = displayTotalApplicableStoreCredit !== '$0.00'

  // Defaults to display.
  let estimatedShipTotal =
    displayShipTotal !== '$0.00' ? displayShipTotal : isBulk ? 'FREE' : null
  let estimatedStoreCreditTotal = isCreditApplied
    ? displayTotalApplicableStoreCredit
    : null
  let estimatedSubtotal =
    displayItemTotal !== displayTotal ? displayItemTotal : null
  let estimatedTaxTotal =
    displayTaxTotal && displayTaxTotal !== '$0.00' ? displayTaxTotal : null
  let estimatedTotal = displayTotal

  // Update defaults when store credit is involved. Necessary because Stripe
  // doesn't fully support store credit, so Chord applies a 10% multipler to
  // cover for the tax, which will get properly calculated later. The totals
  // shown here and in Stripe Checkout don't truly match what the customer ends
  // up paying, which will be slightly lower.
  if (isCreditApplied && !isGiftCardsOnly) {
    let creditPromotions = displayPromotions.reduce(
      (prev, curr) => prev + curr.amount,
      0
    )
    let creditShipping = parseFloat(shipTotal)
    let creditStoreCredit = parseFloat(totalApplicableStoreCredit)
    let creditSubtotal = parseFloat(itemTotal)
    let creditTotal = parseFloat(orderTotalAfterStoreCredit)

    // If the customer hasn't been to checkout yet, but won't get free shipping,
    // it needs to be added for proper totals.
    if (creditShipping === 0 && !eligibleForFreeShipping) {
      creditShipping = 7.99
      creditTotal = creditTotal + creditShipping

      estimatedShipTotal = toUsdCurrency(creditShipping)
    }

    const creditTax = (creditPromotions + creditShipping + creditSubtotal) * 0.1

    creditTotal = creditTotal + creditTax

    // Deduct additional credit that will eventually apply to shipping.
    let creditRemaining = parseFloat(
      displayStoreCreditRemainingAfterCapture.replace('$', '')
    )
    if (creditRemaining) {
      let creditToAdd = Math.min(creditRemaining, creditTotal)

      creditStoreCredit = creditStoreCredit + creditToAdd
      creditTotal = creditTotal - creditToAdd
    }

    estimatedStoreCreditTotal = `-${toUsdCurrency(creditStoreCredit, true)}`
    estimatedSubtotal = toUsdCurrency(creditSubtotal, true)
    estimatedTaxTotal = toUsdCurrency(creditTax, true)
    estimatedTotal = toUsdCurrency(creditTotal, true)
  }

  return (
    <Box py={2} px={[2, 3]}>
      {estimatedSubtotal && (
        <Flex
          mb={[1, 0]}
          variant="text.body2"
          sx={{ justifyContent: 'space-between' }}
        >
          <Text mr={2}>{translate('cart.footer.subtotal')}</Text>
          <Text sx={{ fontWeight: 'medium' }}>{estimatedSubtotal}</Text>
        </Flex>
      )}
      {displayPromotions.map(({ id, label, displayAmount }) => {
        return (
          <Flex
            key={id}
            mb={[1, 0]}
            variant="text.body2"
            sx={{ justifyContent: 'space-between' }}
          >
            <Text mr={2}>{label}:</Text>
            <Text sx={{ flexShrink: '0', fontWeight: 'medium' }}>
              {displayAmount}
            </Text>
          </Flex>
        )
      })}
      {estimatedShipTotal && (
        <Flex
          mb={[1, 0]}
          variant="text.body2"
          sx={{ justifyContent: 'space-between' }}
        >
          <Text mr={2}>{translate('cart.footer.shipping')}</Text>
          <Text sx={{ fontWeight: 'medium' }}>{estimatedShipTotal}</Text>
        </Flex>
      )}
      {estimatedTaxTotal && (
        <Flex
          mb={[1, 0]}
          variant="text.body2"
          sx={{ justifyContent: 'space-between' }}
        >
          <Text mr={2}>{translate('cart.footer.tax')}</Text>
          <Text sx={{ fontWeight: 'medium' }}>{estimatedTaxTotal}</Text>
        </Flex>
      )}
      {estimatedStoreCreditTotal && (
        <Flex
          mb={[1, 0]}
          variant="text.body2"
          sx={{ justifyContent: 'space-between' }}
        >
          <Text mr={2}>{translate('cart.footer.credit')}</Text>
          <Text sx={{ fontWeight: 'medium' }}>{estimatedStoreCreditTotal}</Text>
        </Flex>
      )}
      <Flex
        mb="4px"
        variant="text.body2"
        sx={{ justifyContent: 'space-between' }}
      >
        <Text mr={2}>{translate('cart.footer.estimated_total')}</Text>
        <Text sx={{ fontWeight: 'medium' }}>{estimatedTotal}</Text>
      </Flex>
      <Text as="p" color="greyDark" variant="finePrint">
        {translate('cart.footer.calculated')}
      </Text>
      {!isBulk && !isGiftCardsOnly && (
        <Box
          my={2}
          sx={{
            borderBottom: '1px solid',
            borderTop: '1px solid',
            borderColor: 'greyLight',
          }}
        >
          <GiftMessage giftNote={giftNote} isOverlay={isGiftMessageOverlay} />
        </Box>
      )}
      <Box mt={2} mb={0}>
        <CheckoutButton isBulk={isBulk} />
      </Box>
    </Box>
  )
}

export default CartFooter
